import { useState } from 'react';
import { SEO } from '@/components/SEO';
import { Container } from '@/components/Container';
import { BuscaPassagensWidget } from '@/components/BuscaPassagensWidget';
import { getWhatsAppUrl } from '@/data/loader';
import { useAnalytics } from '@/hooks/useAnalytics';
import { createHowToSchema, createFAQSchema, createBreadcrumbSchema, SITE } from '@/lib/seo-utils';

/* ═══════════════════════════════════════════
   COMO CHEGAR — Página standalone
   Inspirada em fimdatrilha.com.br/como-chegar
   ═══════════════════════════════════════════ */

type Origin = 'curitiba' | 'outros';
type Transport = 'carro' | 'onibus' | 'aviao';
type Season = 'alta' | 'media' | 'baixa';

interface PlanEstimate {
  time: string;
  costRange: string;
  details: string[];
}

const planEstimates: Record<Origin, Partial<Record<Transport, PlanEstimate>>> = {
  curitiba: {
    carro: {
      time: '~3h30',
      costRange: 'R$ 150–280',
      details: [
        'Pedágio BR-277: ~R$ 50 (ida)',
        'Gasolina ~200km: ~R$ 100–150',
        'Estacionamento Pontal do Sul: R$ 40–80/dia',
      ],
    },
    onibus: {
      time: '~3h30',
      costRange: 'R$ 50–80',
      details: [
        'Saída: Terminal Rodoviário de Curitiba',
        'Destino: Pontal do Sul ou Paranaguá',
        'Empresas: Viação Salineira / Colitur',
      ],
    },
  },
  outros: {
    carro: {
      time: '4–8h (varia pela origem)',
      costRange: 'R$ 200–600',
      details: [
        'Via BR-277 até Pontal do Sul (recomendado) ou Paranaguá',
        'Consulte combustível + pedágio da sua cidade de origem',
      ],
    },
    onibus: {
      time: '4–14h (varia)',
      costRange: 'R$ 80–350',
      details: [
        'Conexões de SP, PR interior, SC e RS',
        'Destino final: Paranaguá ou Pontal do Sul',
      ],
    },
    aviao: {
      time: '~6h (total porta a porta)',
      costRange: 'R$ 400–1.200',
      details: [
        'Voo até Curitiba (CWB) — sem voos diretos para Paranaguá',
        'Do aeroporto: ônibus ou carro até o embarque (~1h40)',
      ],
    },
  },
};

interface ScheduleSlot {
  saida: string;
  obs?: string;
  isLastDeparture?: boolean;
}

const horariosPorTemporada: Record<Season, ScheduleSlot[]> = {
  alta: [
    { saida: '07:00' },
    { saida: '08:00' },
    { saida: '09:30' },
    { saida: '11:00' },
    { saida: '13:00' },
    { saida: '15:00' },
    { saida: '17:00', obs: 'Última saída (maioria dos dias)', isLastDeparture: true },
    { saida: '19:00', obs: 'Apenas jan/fev e feriados prolongados' },
  ],
  media: [
    { saida: '08:00' },
    { saida: '09:30' },
    { saida: '11:00' },
    { saida: '13:00' },
    { saida: '15:00' },
    { saida: '17:00', obs: 'Última saída', isLastDeparture: true },
  ],
  baixa: [
    { saida: '09:30' },
    { saida: '11:00' },
    { saida: '13:00' },
    { saida: '15:00' },
    { saida: '17:00', obs: 'Última saída — atenção!', isLastDeparture: true },
  ],
};

interface Cidade {
  id: string;
  label: string;
  tempo: string;
  custo: string;
  via: string;
  dica: string;
}

const cidades: Cidade[] = [
  {
    id: 'curitiba',
    label: 'De Curitiba',
    tempo: '~3h30',
    custo: 'R$ 50–280',
    via: 'BR-277 → Pontal do Sul',
    dica: 'Saída pela BR-277 em direção ao litoral. Pontal do Sul fica ~130km do centro de Curitiba. De ônibus: Terminal Rodoviário, empresas Viação Salineira ou Colitur.',
  },
  {
    id: 'litoral-pr',
    label: 'Do Litoral PR',
    tempo: '30min–2h',
    custo: 'R$ 20–80',
    via: 'PR-508 → Pontal do Sul ou Paranaguá',
    dica: 'De Paranaguá você pode pegar a balsa diretamente (~1h45). De Guaratuba, Pontal do Sul fica a ~40min. De Matinhos, ~20min.',
  },
  {
    id: 'sao-paulo',
    label: 'De São Paulo',
    tempo: '~6–7h',
    custo: 'R$ 150–500',
    via: 'Anhanguera/Bandeirantes + BR-376 + BR-277',
    dica: 'Via Curitiba (a mais comum) ou direto pela BR-116. De ônibus: Terminal Tietê ou Barra Funda com destino Curitiba ou Paranaguá.',
  },
  {
    id: 'sc',
    label: 'De Santa Catarina',
    tempo: '~5–8h',
    custo: 'R$ 100–350',
    via: 'BR-101 → BR-376 → PR-508',
    dica: 'De Joinville (~3h), Blumenau (~3h30), Florianópolis (~5h30). Vá direto para Pontal do Sul pela BR-376.',
  },
  {
    id: 'norte-ne',
    label: 'Do Norte / Nordeste',
    tempo: '~10–24h',
    custo: 'R$ 400–1.200',
    via: 'Avião até CWB + carro/ônibus até Pontal do Sul',
    dica: 'Avião até Curitiba (CWB) é a opção mais prática. Do aeroporto até Pontal do Sul: ~1h40 de carro ou ônibus com conexão.',
  },
  {
    id: 'interior-pr',
    label: 'Do Interior PR',
    tempo: '~4–6h',
    custo: 'R$ 80–280',
    via: 'BR-277 passando por Curitiba',
    dica: 'De Londrina (~6h), Maringá (~6h), Cascavel (~5h). Passe por Curitiba ou vá pela BR-376 direto ao litoral.',
  },
];

interface FaqItem {
  q: string;
  a: string;
}

const faqs: FaqItem[] = [
  {
    q: 'Quanto custa a passagem de balsa para a Ilha do Mel?',
    a: 'De Pontal do Sul: R$ 44,18 ida e volta (valor 2026). De Paranaguá: R$ 76,50 ida e volta. As passagens são compradas no guichê no dia — não há sistema online oficial.',
  },
  {
    q: 'Quantas barcas saem por dia para a Ilha do Mel?',
    a: 'Na alta temporada (jan–mar, jul, dez) saem até 8 horários por dia (07:00 às 17:00 ou 19:00). Na baixa temporada, são 5 horários (09:30 às 17:00). A última saída é sempre ao redor das 17:00.',
  },
  {
    q: 'Posso levar o carro para a Ilha do Mel?',
    a: 'Não. A Ilha do Mel é uma área de preservação ambiental e não permite a entrada de veículos motorizados. Deixe o carro nos estacionamentos pagos em Pontal do Sul (R$ 40–80/dia) ou Paranaguá.',
  },
  {
    q: 'Qual a diferença entre embarcar em Pontal do Sul e Paranaguá?',
    a: 'Pontal do Sul: travessia de 30–35 min, R$ 44,18, mais recomendado — mais próximo de Curitiba e com mais horários. Paranaguá: travessia de 1h45, R$ 76,50, vale a pena se você já está na cidade histórica e quer aproveitar o passeio de barco pela baía.',
  },
  {
    q: 'Preciso comprar a passagem de balsa com antecedência?',
    a: 'Não há sistema de reserva online oficial — a compra é presencial no guichê. Na alta temporada, chegue cedo (30–60 min antes) pois os horários lotam rapidamente, especialmente fins de semana e feriados.',
  },
  {
    q: 'Posso levar mala de rodinha para a Ilha do Mel?',
    a: 'Não é recomendado. As ruas da ilha são de areia ou calçamento irregular — rodinhas não funcionam. Prefira mochila ou bolsa de lona. Bagagem maior pode ser carregada por barqueiros locais (custo extra).',
  },
];

/* ─── Schemas ─── */

const howToSchema = createHowToSchema(
  'Como chegar na Ilha do Mel',
  'Guia completo passo a passo para chegar na Ilha do Mel saindo de qualquer cidade do Brasil',
  'PT4H',
  [
    {
      name: 'Chegue ao litoral do Paraná',
      text: 'Vá até Pontal do Sul (recomendado) ou Paranaguá, no litoral do Paraná. De Curitiba são ~130km pela BR-277.',
      url: `${SITE.url}/como-chegar#rotas`,
    },
    {
      name: 'Estacione e vá ao trapiche',
      text: 'Deixe o carro nos estacionamentos pagos próximos ao trapiche (~R$ 50–80/dia). Malas com rodinhas não funcionam na ilha — prefira mochila.',
      url: `${SITE.url}/como-chegar#travessia`,
    },
    {
      name: 'Compre a passagem no guichê',
      text: 'Compre a passagem no balcão em Pontal do Sul. Preço 2026: R$ 44,18 (ida e volta). Na alta temporada chegue com antecedência.',
      url: `${SITE.url}/como-chegar#horarios`,
    },
    {
      name: 'Embarque na balsa',
      text: 'A travessia de Pontal do Sul dura 30–35 minutos até a Ilha do Mel. Aproveite a vista da Baía de Paranaguá!',
      url: `${SITE.url}/como-chegar#travessia`,
    },
  ],
  { currency: 'BRL', value: '44.18' }
);

const schemas = [
  howToSchema,
  createFAQSchema(faqs.map(f => ({ q: f.q, a: f.a }))),
  createBreadcrumbSchema([
    { name: 'Início', path: '/' },
    { name: 'Como Chegar', path: '/como-chegar' },
  ]),
];

/* ─── Sub-componentes ─── */

function StatChip({ value, label }: { value: string; label: string }) {
  return (
    <div className="bg-white/15 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-3 text-center min-w-[100px]">
      <div className="font-display text-2xl text-white leading-none">{value}</div>
      <div className="font-body text-xs text-white/80 mt-1 leading-tight">{label}</div>
    </div>
  );
}

function SectionLabel({ children }: { children: string }) {
  return (
    <span className="font-body text-xs font-bold uppercase tracking-widest text-coral">
      {children}
    </span>
  );
}

function CheckIcon({ className = 'text-teal-deep' }: { className?: string }) {
  return (
    <svg
      className={`w-4 h-4 mt-0.5 flex-shrink-0 ${className}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg
      className="w-4 h-4 mt-0.5 flex-shrink-0 text-coral"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

/* ═══════════════════════════════════════════
   PÁGINA PRINCIPAL
   ═══════════════════════════════════════════ */

export function ComoChegar() {
  const { trackWhatsapp } = useAnalytics();
  const [origin, setOrigin] = useState<Origin>('curitiba');
  const [transport, setTransport] = useState<Transport>('carro');
  const [season, setSeason] = useState<Season>('media');
  const [openCity, setOpenCity] = useState<string | null>('curitiba');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const currentEstimate = planEstimates[origin][transport];
  const horarios = horariosPorTemporada[season];

  const handleOriginChange = (o: Origin) => {
    setOrigin(o);
    if (o === 'curitiba' && transport === 'aviao') setTransport('carro');
  };

  return (
    <>
      <SEO
        title="Como Chegar na Ilha do Mel 2026 — Balsa, Horários e Rotas"
        description="Guia completo de como chegar na Ilha do Mel: balsas de Pontal do Sul e Paranaguá, horários por temporada, rotas de carro/ônibus/avião e quanto custa a travessia."
        keywords="como chegar ilha do mel, balsa ilha do mel, pontal do sul ilha do mel, paranagua ilha do mel, horários balsa ilha do mel"
        canonical="/como-chegar"
        jsonLd={{ '@context': 'https://schema.org', '@graph': schemas }}
      />

      {/* ── Hero ── */}
      <section className="relative min-h-[72vh] flex items-end overflow-hidden">
        <img
          src="/images/sailboat.jpg"
          alt="Balsa se aproximando da Ilha do Mel"
          className="absolute inset-0 w-full h-full object-cover object-center"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/10" />

        <Container className="relative z-10 pb-14 pt-32">
          {/* Breadcrumb */}
          <nav aria-label="Navegação estrutural" className="mb-8">
            <ol className="flex items-center gap-2 font-body text-sm text-white/60">
              <li>
                <a href="/" className="hover:text-white transition-colors">
                  Ilha do Mel
                </a>
              </li>
              <li aria-hidden="true" className="text-white/30">
                ›
              </li>
              <li className="text-white font-medium">Como Chegar</li>
            </ol>
          </nav>

          <SectionLabel>Guia de Acesso</SectionLabel>

          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white mt-3 mb-4 leading-tight max-w-4xl">
            Como Chegar na
            <br className="hidden sm:block" /> Ilha do Mel
          </h1>
          <p className="font-editorial text-xl text-white/85 max-w-2xl mb-8 leading-relaxed">
            Tudo o que você precisa saber — balsas, rotas, horários e quanto vai custar a travessia.
          </p>

          {/* Stat chips */}
          <div className="flex flex-wrap gap-3">
            <StatChip value="30–35 min" label="Travessia de Pontal" />
            <StatChip value="R$ 44" label="Passagem ida e volta" />
            <StatChip value="5–8x/dia" label="Saídas por temporada" />
          </div>
        </Container>
      </section>

      {/* ── Jornada Overview ── */}
      <section className="py-16 bg-white border-b border-sand-100">
        <Container>
          <div className="text-center mb-10">
            <SectionLabel>A Jornada</SectionLabel>
            <h2 className="font-display text-4xl text-warm-dark mt-2">3 passos até a ilha</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto relative">
            {/* Linha conectora no desktop */}
            <div className="hidden md:block absolute top-8 left-[calc(16.66%+2rem)] right-[calc(16.66%+2rem)] h-px bg-gradient-to-r from-teal-200 via-teal-300 to-teal-200" />

            {[
              {
                num: '01',
                icon: '🚗',
                title: 'Chegue ao Litoral',
                desc: 'Dirija ou pegue ônibus até Pontal do Sul (recomendado) ou Paranaguá. De Curitiba são ~130km pela BR-277.',
                detail: '~1h40 de Curitiba',
              },
              {
                num: '02',
                icon: '⚓',
                title: 'Embarque na Balsa',
                desc: 'Compre a passagem no guichê e embarque. Sem carros na ilha — deixe o veículo no estacionamento.',
                detail: 'R$ 44,18 ida e volta',
              },
              {
                num: '03',
                icon: '🌴',
                title: 'Chegue na Ilha',
                desc: 'Após 30 minutos de travessia pela Baía de Paranaguá, você desembarca direto no paraíso.',
                detail: '30–35 min de travessia',
              },
            ].map(step => (
              <div key={step.num} className="text-center relative">
                <div className="w-16 h-16 rounded-full bg-teal-deep text-white font-display text-2xl flex items-center justify-center mx-auto mb-4 relative z-10 shadow-lg">
                  {step.num}
                </div>
                <div className="text-3xl mb-3">{step.icon}</div>
                <h3 className="font-display text-xl text-warm-dark mb-2">{step.title}</h3>
                <p className="font-body text-sm text-warm-gray leading-relaxed mb-3">{step.desc}</p>
                <span className="inline-block bg-sand-100 text-warm-gray font-body text-xs font-semibold px-3 py-1 rounded-full">
                  {step.detail}
                </span>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ── Travessia: Pontal vs Paranaguá ── */}
      <section id="travessia" className="py-16 bg-sand-50">
        <Container>
          <div className="text-center mb-10">
            <SectionLabel>Opções de Embarque</SectionLabel>
            <h2 className="font-display text-4xl text-warm-dark mt-2">Por onde embarcar?</h2>
            <p className="font-editorial text-lg text-warm-gray mt-2">
              Dois trapiches atendem a ilha — escolha o que faz mais sentido para a sua rota.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Pontal do Sul */}
            <div className="bg-white rounded-2xl border-2 border-teal-200 p-7 shadow-sm relative overflow-hidden">
              <span className="absolute top-4 right-4 bg-teal-deep text-white text-xs font-body font-bold px-3 py-1 rounded-full">
                Recomendado
              </span>
              <h3 className="font-display text-2xl text-warm-dark mb-1">Pontal do Sul</h3>
              <p className="font-body text-sm text-warm-gray mb-5">
                Mais próximo de Curitiba, mais frequente
              </p>

              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-teal-50 rounded-xl p-3 text-center">
                  <div className="font-display text-xl text-teal-deep">30–35 min</div>
                  <div className="font-body text-xs text-warm-gray">Travessia</div>
                </div>
                <div className="bg-sand-50 rounded-xl p-3 text-center">
                  <div className="font-display text-xl text-coral">R$ 44,18</div>
                  <div className="font-body text-xs text-warm-gray">Ida e volta</div>
                </div>
                <div className="bg-sand-50 rounded-xl p-3 text-center">
                  <div className="font-display text-lg text-warm-dark">5–8x/dia</div>
                  <div className="font-body text-xs text-warm-gray">Frequência</div>
                </div>
                <div className="bg-sand-50 rounded-xl p-3 text-center">
                  <div className="font-display text-lg text-warm-dark">07h–17h</div>
                  <div className="font-body text-xs text-warm-gray">Horários</div>
                </div>
              </div>

              <ul className="space-y-2.5">
                {[
                  'Mais próximo de Curitiba (~130km)',
                  'Mais frequência de horários',
                  'Travessia mais rápida',
                  'Estacionamento fácil disponível',
                ].map(v => (
                  <li key={v} className="flex items-start gap-2">
                    <CheckIcon />
                    <span className="font-body text-sm text-warm-gray">{v}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Paranaguá */}
            <div className="bg-white rounded-2xl border-2 border-sand-200 p-7 shadow-sm">
              <h3 className="font-display text-2xl text-warm-dark mb-1">Paranaguá</h3>
              <p className="font-body text-sm text-warm-gray mb-5">
                Vale o passeio pela baía histórica
              </p>

              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-sand-100 rounded-xl p-3 text-center">
                  <div className="font-display text-xl text-warm-dark">1h45</div>
                  <div className="font-body text-xs text-warm-gray">Travessia</div>
                </div>
                <div className="bg-sand-100 rounded-xl p-3 text-center">
                  <div className="font-display text-xl text-coral">R$ 76,50</div>
                  <div className="font-body text-xs text-warm-gray">Ida e volta</div>
                </div>
                <div className="bg-sand-100 rounded-xl p-3 text-center">
                  <div className="font-display text-lg text-warm-dark">2–3x/dia</div>
                  <div className="font-body text-xs text-warm-gray">Frequência</div>
                </div>
                <div className="bg-sand-100 rounded-xl p-3 text-center">
                  <div className="font-display text-lg text-warm-dark">8h–15h</div>
                  <div className="font-body text-xs text-warm-gray">Horários</div>
                </div>
              </div>

              <ul className="space-y-2.5">
                {[
                  'Passeio panorâmico pela Baía de Paranaguá',
                  'Ideal se você já está na cidade histórica',
                  'Ótimo para viagem de navio/cruzeiro',
                ].map(v => (
                  <li key={v} className="flex items-start gap-2">
                    <CheckIcon />
                    <span className="font-body text-sm text-warm-gray">{v}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </section>

      {/* ── Planejador de Rota ── */}
      <section id="rotas" className="py-16 bg-white">
        <Container>
          <div className="text-center mb-10">
            <SectionLabel>Planejador Interativo</SectionLabel>
            <h2 className="font-display text-4xl text-warm-dark mt-2">Calcule sua rota</h2>
            <p className="font-editorial text-lg text-warm-gray mt-2">
              Selecione sua origem e transporte para ver o tempo e custo estimados.
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            {/* Seletor de origem */}
            <div className="mb-5">
              <p className="font-body text-xs font-bold uppercase tracking-widest text-warm-gray mb-3">
                Saindo de
              </p>
              <div className="flex gap-3 flex-wrap">
                {(['curitiba', 'outros'] as Origin[]).map(o => (
                  <button
                    key={o}
                    aria-pressed={origin === o}
                    onClick={() => handleOriginChange(o)}
                    className={`px-5 py-2.5 rounded-full font-body text-sm font-semibold transition-all min-h-[44px] ${
                      origin === o
                        ? 'bg-teal-deep text-white shadow-md'
                        : 'bg-white border border-sand-200 text-warm-gray hover:border-teal-300 hover:text-teal-deep'
                    }`}
                  >
                    {o === 'curitiba' ? 'Curitiba' : 'Outras cidades'}
                  </button>
                ))}
              </div>
            </div>

            {/* Seletor de transporte */}
            <div className="mb-6">
              <p className="font-body text-xs font-bold uppercase tracking-widest text-warm-gray mb-3">
                Transporte
              </p>
              <div className="flex gap-3 flex-wrap">
                {(
                  [
                    { id: 'carro', label: '🚗 Carro' },
                    { id: 'onibus', label: '🚌 Ônibus' },
                    { id: 'aviao', label: '✈️ Avião' },
                  ] as { id: Transport; label: string }[]
                ).map(tr => {
                  const disabled = origin === 'curitiba' && tr.id === 'aviao';
                  return (
                    <button
                      key={tr.id}
                      aria-pressed={transport === tr.id}
                      disabled={disabled}
                      onClick={() => !disabled && setTransport(tr.id)}
                      className={`px-5 py-2.5 rounded-full font-body text-sm font-semibold transition-all min-h-[44px] ${
                        disabled
                          ? 'opacity-40 cursor-not-allowed bg-sand-50 border border-sand-200 text-warm-gray'
                          : transport === tr.id
                            ? 'bg-teal-deep text-white shadow-md'
                            : 'bg-white border border-sand-200 text-warm-gray hover:border-teal-300 hover:text-teal-deep'
                      }`}
                    >
                      {tr.label}
                    </button>
                  );
                })}
              </div>
              {origin === 'curitiba' && (
                <p className="font-body text-xs text-warm-gray mt-2 opacity-75">
                  ✈️ Avião não disponível saindo de Curitiba
                </p>
              )}
            </div>

            {/* Card de resultado */}
            <div aria-live="polite">
              {currentEstimate ? (
                <div className="bg-gradient-to-br from-teal-50 to-sand-50 rounded-2xl border border-teal-100 p-6 shadow-sm">
                  <div className="grid grid-cols-2 gap-4 mb-5">
                    <div className="bg-white rounded-xl p-4 text-center shadow-sm">
                      <div className="font-display text-2xl text-teal-deep">
                        {currentEstimate.time}
                      </div>
                      <div className="font-body text-xs text-warm-gray mt-1">Tempo estimado</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 text-center shadow-sm">
                      <div className="font-display text-2xl text-coral">
                        {currentEstimate.costRange}
                      </div>
                      <div className="font-body text-xs text-warm-gray mt-1">Custo estimado</div>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {currentEstimate.details.map((d, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-teal-400 mt-2 flex-shrink-0" />
                        <span className="font-body text-sm text-warm-gray">{d}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 pt-4 border-t border-teal-100 text-center">
                    <p className="font-body text-xs text-warm-gray mb-1">
                      + R$ 44,18 de passagem de balsa (ida e volta)
                    </p>
                    <p className="font-body text-xs text-warm-gray">
                      + Estacionamento em Pontal do Sul: R$ 40–80/dia (se vier de carro)
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </Container>
      </section>

      {/* ── Por cidade de origem ── */}
      <section className="py-16 bg-sand-50">
        <Container>
          <div className="text-center mb-10">
            <SectionLabel>Por Cidade</SectionLabel>
            <h2 className="font-display text-4xl text-warm-dark mt-2">Como chegar da sua cidade</h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-3">
            {cidades.map(cidade => (
              <div
                key={cidade.id}
                className="bg-white rounded-2xl border border-sand-200 overflow-hidden shadow-sm"
              >
                <button
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                  onClick={() => setOpenCity(openCity === cidade.id ? null : cidade.id)}
                  aria-expanded={openCity === cidade.id}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="font-display text-lg text-warm-dark truncate">
                      {cidade.label}
                    </div>
                    <div className="hidden sm:flex gap-2 flex-shrink-0">
                      <span className="bg-teal-50 text-teal-deep text-xs font-body font-semibold px-2.5 py-1 rounded-full">
                        {cidade.tempo}
                      </span>
                      <span className="bg-sand-100 text-warm-gray text-xs font-body font-semibold px-2.5 py-1 rounded-full">
                        {cidade.custo}
                      </span>
                    </div>
                  </div>
                  <svg
                    className={`w-5 h-5 text-warm-gray flex-shrink-0 transition-transform duration-200 ${openCity === cidade.id ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {openCity === cidade.id && (
                  <div className="px-6 pb-6 border-t border-sand-100">
                    <div className="pt-4 flex flex-wrap gap-2 mb-4 sm:hidden">
                      <span className="bg-teal-50 text-teal-deep text-xs font-body font-semibold px-2.5 py-1 rounded-full">
                        {cidade.tempo}
                      </span>
                      <span className="bg-sand-100 text-warm-gray text-xs font-body font-semibold px-2.5 py-1 rounded-full">
                        {cidade.custo}
                      </span>
                    </div>
                    <div className="flex items-start gap-2 mb-3 pt-4 sm:pt-0">
                      <span className="text-warm-gray font-body text-xs font-semibold uppercase tracking-wider flex-shrink-0">
                        Via:
                      </span>
                      <span className="font-body text-sm text-warm-dark">{cidade.via}</span>
                    </div>
                    <p className="font-body text-sm text-warm-gray leading-relaxed">
                      {cidade.dica}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ── Horários da Balsa ── */}
      <section id="horarios" className="py-16 bg-white">
        <Container>
          <div className="text-center mb-10">
            <SectionLabel>Horários 2026</SectionLabel>
            <h2 className="font-display text-4xl text-warm-dark mt-2">Balsas de Pontal do Sul</h2>
            <p className="font-editorial text-lg text-warm-gray mt-2">
              Os horários variam conforme a temporada — confira abaixo.
            </p>
          </div>

          <div className="max-w-lg mx-auto">
            {/* Tabs de temporada */}
            <div role="tablist" className="flex gap-2 mb-6 flex-wrap">
              {(
                [
                  { id: 'alta', label: 'Alta', sub: 'jan–mar · jul · dez' },
                  { id: 'media', label: 'Média', sub: 'abr · jun · ago–set' },
                  { id: 'baixa', label: 'Baixa', sub: 'mai · out–nov' },
                ] as { id: Season; label: string; sub: string }[]
              ).map(s => (
                <button
                  key={s.id}
                  role="tab"
                  aria-selected={season === s.id}
                  onClick={() => setSeason(s.id)}
                  className={`flex-1 min-w-[100px] px-3 py-3 rounded-xl font-body text-sm font-semibold transition-all min-h-[52px] text-center ${
                    season === s.id
                      ? 'bg-teal-deep text-white shadow-md'
                      : 'bg-sand-50 border border-sand-200 text-warm-gray hover:border-teal-300'
                  }`}
                >
                  <div>{s.label}</div>
                  <div
                    className={`text-xs font-normal mt-0.5 ${season === s.id ? 'text-teal-100' : 'text-warm-gray/60'}`}
                  >
                    {s.sub}
                  </div>
                </button>
              ))}
            </div>

            {/* Tabela */}
            <div className="bg-white rounded-2xl border border-sand-200 overflow-hidden shadow-sm">
              <table className="w-full font-body text-sm">
                <thead>
                  <tr className="bg-sand-50 border-b border-sand-200">
                    <th className="text-left py-3 px-5 font-semibold text-warm-dark">Saída</th>
                    <th className="text-left py-3 px-5 font-semibold text-warm-dark">Observação</th>
                  </tr>
                </thead>
                <tbody>
                  {horarios.map(h => (
                    <tr
                      key={h.saida}
                      className={`border-b border-sand-100 last:border-0 ${h.isLastDeparture ? 'bg-coral/5' : ''}`}
                    >
                      <td
                        className={`py-3 px-5 font-display text-lg ${h.isLastDeparture ? 'text-coral' : 'text-warm-dark'}`}
                      >
                        {h.isLastDeparture && <span className="mr-1 text-sm">⚠️</span>}
                        {h.saida}
                      </td>
                      <td className="py-3 px-5 text-warm-gray">{h.obs ?? 'Diário'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="font-body text-xs text-warm-gray text-center mt-3 leading-relaxed">
              Horários sujeitos a alteração por condições climáticas ou manutenção. Confirme antes
              de viajar.
            </p>

            {/* Alerta de antecedência */}
            <div className="mt-5 bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
              <span className="text-xl flex-shrink-0">⏰</span>
              <div>
                <p className="font-body text-sm font-semibold text-warm-dark">
                  Chegue com antecedência na alta temporada
                </p>
                <p className="font-body text-sm text-warm-gray mt-0.5">
                  Os horários lotam rápido em fins de semana e feriados. Recomendamos chegar ao
                  trapiche 30–60 minutos antes.
                </p>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ── Alertas Importantes ── */}
      <section className="py-12 bg-sand-50">
        <Container>
          <div className="max-w-3xl mx-auto space-y-4">
            <div className="bg-white border border-amber-200 rounded-xl p-5 flex gap-4 shadow-sm">
              <span className="text-2xl flex-shrink-0">⚠️</span>
              <div>
                <p className="font-body text-sm font-semibold text-warm-dark mb-1">
                  Cuidado com barqueiros irregulares
                </p>
                <p className="font-body text-sm text-warm-gray">
                  Ao chegar a Pontal do Sul, barqueiros particulares podem oferecer transporte mais
                  barato. Use sempre a balsa oficial — é mais segura e tem preço tabelado.
                </p>
              </div>
            </div>
            <div className="bg-white border border-amber-200 rounded-xl p-5 flex gap-4 shadow-sm">
              <span className="text-2xl flex-shrink-0">🧳</span>
              <div>
                <p className="font-body text-sm font-semibold text-warm-dark mb-1">
                  Esqueça as malas com rodinhas
                </p>
                <p className="font-body text-sm text-warm-gray">
                  As ruas da ilha são de areia ou calçamento irregular. Malas com rodas não
                  funcionam — use mochila ou bolsa de lona. Bagagem grande pode ser carregada por
                  barqueiros (custo extra).
                </p>
              </div>
            </div>
            <div className="bg-white border border-teal-100 rounded-xl p-5 flex gap-4 shadow-sm">
              <span className="text-2xl flex-shrink-0">💊</span>
              <div>
                <p className="font-body text-sm font-semibold text-warm-dark mb-1">
                  Enjoo na travessia?
                </p>
                <p className="font-body text-sm text-warm-gray">
                  A Baía de Paranaguá pode ficar agitada com vento. Se você sofre de cinetose, tome
                  um remédio 30 minutos antes do embarque. Fique no convés olhando para o horizonte.
                </p>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ── O que levar / não levar ── */}
      <section className="py-16 bg-white">
        <Container>
          <div className="text-center mb-10">
            <SectionLabel>Preparação</SectionLabel>
            <h2 className="font-display text-4xl text-warm-dark mt-2">O que levar na mala</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Levar */}
            <div className="bg-teal-50 rounded-2xl p-6 border border-teal-100">
              <h3 className="font-display text-xl text-teal-deep mb-4 flex items-center gap-2">
                <span className="text-2xl">✅</span> Leve isso
              </h3>
              <ul className="space-y-2.5">
                {[
                  'Protetor solar (mínimo FPS 50)',
                  'Repelente de insetos',
                  'Dinheiro em espécie (ilha tem pouco ATM)',
                  'Roupas leves e de secagem rápida',
                  'Tênis ou sandália para trilhas',
                  'Garrafa de água reutilizável',
                  'Lanterna ou carregador portátil',
                  'Remédio para enjoo',
                ].map(item => (
                  <li key={item} className="flex items-start gap-2.5">
                    <CheckIcon className="text-teal-deep" />
                    <span className="font-body text-sm text-warm-dark">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Não levar */}
            <div className="bg-red-50 rounded-2xl p-6 border border-red-100">
              <h3 className="font-display text-xl text-coral mb-4 flex items-center gap-2">
                <span className="text-2xl">🚫</span> Deixe em casa
              </h3>
              <ul className="space-y-2.5">
                {[
                  'Carro — veículos não entram na ilha',
                  'Mala com rodinhas (areia e pedra irregular)',
                  'Objetos de valor desnecessários',
                  'Salto alto ou sapatos fechados',
                  'Expectativa de sinal 4G constante',
                  'Pet sem pesquisa prévia sobre regras',
                ].map(item => (
                  <li key={item} className="flex items-start gap-2.5">
                    <XIcon />
                    <span className="font-body text-sm text-warm-dark">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 bg-sand-50">
        <Container>
          <div className="text-center mb-10">
            <SectionLabel>Dúvidas Frequentes</SectionLabel>
            <h2 className="font-display text-4xl text-warm-dark mt-2">
              Perguntas sobre como chegar
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-sand-200 overflow-hidden shadow-sm"
              >
                <button
                  className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                >
                  <span className="font-body text-sm font-semibold text-warm-dark leading-snug">
                    {faq.q}
                  </span>
                  <svg
                    className={`w-5 h-5 text-warm-gray flex-shrink-0 transition-transform duration-200 mt-0.5 ${openFaq === i ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-6 border-t border-sand-100">
                    <p className="font-body text-sm text-warm-gray leading-relaxed pt-4">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ── CTA Final ── */}
      <section className="py-16 bg-white">
        <Container>
          <div className="max-w-2xl mx-auto text-center mb-10">
            <SectionLabel>Próximo Passo</SectionLabel>
            <h2 className="font-display text-4xl text-warm-dark mt-2 mb-3">Pronto para partir?</h2>
            <p className="font-editorial text-lg text-warm-gray">
              Reserve sua passagem aérea até Curitiba ou tire dúvidas com a gente no WhatsApp.
            </p>
          </div>

          <div className="max-w-xl mx-auto space-y-6">
            <BuscaPassagensWidget />

            <div className="text-center">
              <p className="font-body text-sm text-warm-gray mb-3">
                Ainda tem dúvidas sobre a rota ou quer ajuda para planejar?
              </p>
              <a
                href={getWhatsAppUrl(
                  '41999999999',
                  'Oi! Vi o guia de como chegar na Ilha do Mel e tenho uma dúvida:',
                  'como-chegar-cta'
                )}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsapp({ provider: 'Geral', source: 'como-chegar-cta' })}
                className="inline-flex items-center gap-2.5 bg-[#25D366] hover:bg-[#20bd5a] text-white font-body font-semibold text-sm rounded-xl px-6 py-3.5 min-h-[48px] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg shadow-[#25D366]/20"
              >
                <WhatsAppIcon />
                Fala com a gente no WhatsApp
              </a>
            </div>

            {/* Links relacionados */}
            <div className="bg-sand-50 rounded-2xl p-5 border border-sand-200">
              <p className="font-body text-xs font-bold uppercase tracking-widest text-warm-gray mb-3">
                Continue planejando
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { href: '/', label: 'Ilha do Mel', icon: '📖' },
                  { href: '/pousada', label: 'Onde Ficar', icon: '🏠' },
                  { href: '/praia', label: 'Praias da Ilha', icon: '🏖️' },
                  { href: '/trilha', label: 'Trilhas', icon: '🥾' },
                ].map(link => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="flex items-center gap-2 bg-white rounded-xl p-3 border border-sand-200 hover:border-teal-300 hover:shadow-sm transition-all group"
                  >
                    <span className="text-lg">{link.icon}</span>
                    <span className="font-body text-sm text-warm-gray group-hover:text-teal-deep transition-colors">
                      {link.label}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}
